/* eslint-disable no-underscore-dangle */
/* eslint-disable class-methods-use-this */

import * as digitalData from '@ao-internal/digital-data';

import { MatchedResults } from '../components/Results';
import { AddToBasketMessageContent } from '../components/message-types/AddToBasketMessage';
import { ButtonMessageContent } from '../components/message-types/ButtonMessage';
import { ProductCardMessageContent } from '../components/message-types/ProductCardMessage';
import { RecommendedProductMessageContent } from '../components/message-types/RecommendedProductsMessage';
import { SuggestedPhrasesMessageContent } from '../components/message-types/SuggestedPhrasesMessage';
import { Feature } from '../components/product-specification/types';
import {
	TaggingEventNames,
	isAuditMode,
	isReadOnlyMode,
	isTestMode,
	startLiveChatCspCheckMessage,
} from '../globals';
import { buildApiRequestContext } from '../helpers/context';
import { convertDateStringToTimestamp } from '../helpers/date';
import { getChatHistoryAuditAccessKey } from '../helpers/storage';
import {
	BaseMessageSenderParams,
	SendMessageResponse,
} from '../message-senders/BaseMessageSender';
import {
	ChatHistoryType,
	ChatMessage,
	ChatMessageTypes,
	InputTypes,
} from '../store';
import chatHistoryResponseFixture from './__tests__/chatHistoryResponseFixture.json'; // Used for debugging
import chatQueryResponseFixture from './__tests__/chatQueryResponseFixture.json'; // Used for debugging

export interface ChatHistorySession {
	id: string;
	expiryDate: number;
	username?: string;
	results?: MatchedResults;
	history: ChatHistoryType;
	intent?: string;
}
interface ChatHistorySessionAudit {
	chatHistoryModel: ChatHistorySession;
}
type ChatbotApiHistoryResponse = ChatHistorySession | ChatHistorySessionAudit;

type GenericMessageViewAnalyticsBeacon = {
	type: ChatMessageTypes;
	message?: string | string[] | ObjectLiteral;
};

type ProductCardViewAnalyticsBeacon = {
	productSku: string;
	spotlightFeatures?: string[];
};

export type MessageViewAnalyticsBeacon =
	| GenericMessageViewAnalyticsBeacon
	| ProductCardViewAnalyticsBeacon;

function remoteSessionModelMapper(
	chatbotApiHistoryResponse: ChatbotApiHistoryResponse
): ChatHistorySession {
	if (chatbotApiHistoryResponse.hasOwnProperty('chatHistoryModel')) {
		return (chatbotApiHistoryResponse as ChatHistorySessionAudit)
			.chatHistoryModel;
	}
	return chatbotApiHistoryResponse as ChatHistorySession;
}

export class ChatService {
	async getRemoteSessionState(sessionId: string): Promise<ChatHistorySession> {
		// DEBUG
		// return Promise.resolve(chatHistoryResponseFixture);

		try {
			const headers = new Headers({
				'x-api-key': CHAT_API_KEY,
			} as Record<string, string>);

			if (isAuditMode) {
				const auditAccessKey = getChatHistoryAuditAccessKey();

				if (auditAccessKey) {
					headers.set('x-access-key', auditAccessKey.value);
				} else {
					return Promise.reject('Invalid access key');
				}
			}

			const response = await fetch(
				`${CHAT_API_URL}/chat?sessionId=${sessionId}`,
				{
					method: 'GET',
					headers,
				}
			);

			if (!response.ok) {
				return Promise.reject({ error: response.status });
			}

			const responseData: ChatbotApiHistoryResponse = await response.json();
			const sessionModel = remoteSessionModelMapper(responseData);

			if (sessionModel.history.length === 0) {
				return Promise.reject();
			}

			sessionModel.history = sessionModel.history.map((messageGroup) => {
				return {
					...messageGroup,
					timestamp: convertDateStringToTimestamp(messageGroup.timestamp),
				};
			});

			return Promise.resolve(sessionModel);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async sendMessage({
		sessionId,
		message,
		isSystemMessage,
		inputType,
		groupIntent,
	}: BaseMessageSenderParams): Promise<SendMessageResponse> {
		// DEBUG
		// return Promise.resolve(chatQueryResponseFixture);

		if (isReadOnlyMode) {
			return Promise.reject(Error('Messages cannot be sent in readOnly mode.'));
		}

		try {
			const requestBody = {
				text: message,
				isEvent: isSystemMessage,
				inputType: inputType,
				isTestMode,
				requestContext: buildApiRequestContext(),
			};

			let response = await fetch(`${CHAT_API_URL}/chat`, {
				method: 'POST',
				headers: {
					'x-api-key': CHAT_API_KEY,
				},
				body: JSON.stringify({
					...requestBody,
					sessionId,
				}),
			});

			if (response.status === 404) {
				// The provided sessionId doesn't match any known sessions on the backend
				// So make a second request without a sessionId to allow the backend to generate a new session
				response = await fetch(`${CHAT_API_URL}/chat`, {
					method: 'POST',
					headers: {
						'x-api-key': CHAT_API_KEY,
					},
					body: JSON.stringify({
						...requestBody,
					}),
				});
			}

			if (!response.ok) {
				return Promise.reject({ error: response.status });
			}

			let responseData: SendMessageResponse = await response.json();

			// DEBUG
			// let responseData: SendMessageResponse = chatQueryResponseFixture;

			// TODO: Move up to the send message handler?
			responseData = {
				...responseData,
				responses: responseData.responses.filter(this.isValidMessage),
			};

			const { responses, intent } = responseData;

			const feedbackRequested = responses.find((msg) => msg.requestFeedback)
				? true
				: false;

			if (intent) {
				digitalData.sendBeacon(TaggingEventNames.responseDetail, {
					query: inputType === InputTypes.Text ? 'USER_CONTENT' : message,
					groupIntent,
					intent,
					inputType,
					feedbackRequested,
				});
			}

			// TODO: Move to view components?
			if (responses) {
				responses.forEach((messageData) => {
					let beaconName = TaggingEventNames.responseMessageDetail;
					let beaconData: MessageViewAnalyticsBeacon = {
						type: messageData.type,
					};

					switch (messageData.type) {
						case ChatMessageTypes.Button:
							beaconData.message = (
								messageData.content as ButtonMessageContent[]
							).map((button) => button.replyText);
							break;
						case ChatMessageTypes.SuggestedPhrases:
							beaconData.message = (
								messageData.content as SuggestedPhrasesMessageContent[]
							).map((button) => button.replyText);
							break;
						case ChatMessageTypes.RecommendedProducts:
							beaconData.message = (
								messageData.content as RecommendedProductMessageContent[]
							).map((product) => product.productCode);
							break;
						case ChatMessageTypes.AddToBasket:
							beaconData.message = (
								messageData.content as AddToBasketMessageContent
							).productSummary.productCode;
							break;
						case ChatMessageTypes.ProductCard:
							const productSummary = (
								messageData.content as ProductCardMessageContent
							).productSummary;
							const spotlightFeatures = (
								messageData.content as ProductCardMessageContent
							).featureGroups.reduce(
								(acc, featureGroup) => [
									...acc,
									...featureGroup.features.filter(
										(feature) => feature.isSpotlightFeature
									),
								],
								[] as Feature[]
							);
							beaconName = TaggingEventNames.productCardView;
							beaconData = {
								productSku: productSummary.productCode,
								spotlightFeatures: spotlightFeatures.map(
									(feature) => feature.featureName
								),
							};
							break;
						default:
							if (
								window.DY &&
								window.DY.API &&
								messageData.content === startLiveChatCspCheckMessage
							) {
								// eslint-disable-next-line new-cap
								window.DY.API('event', {
									name: 'chatbot started livechat csp check',
									properties: {},
								});
							}
							beaconData.message = messageData.content;
							break;
					}

					digitalData.sendBeacon(beaconName, beaconData);
				});
			}

			return Promise.resolve(responseData);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	isValidMessage(message: ChatMessage): boolean {
		if (!message || message.type === null || message.type === undefined) {
			return false;
		}

		if (!ChatMessageTypes[message.type]) {
			return false;
		}

		return true;
	}
}

export default ChatService;
